/**
 * One-shot sticky signal primitive.
 *
 *   awaitFor(AwaitFor.X, cb)         — register; runs immediately if already completed
 *   completeAwait(AwaitFor.X, value) — store value, drain pending callbacks
 *   isAwaitCompleted(AwaitFor.X)     — synchronous "has it resolved" check
 *
 * Semantics:
 * - Sticky: late `awaitFor` after `completeAwait` runs immediately on the
 *   stored value.
 * - Synchronous invocation. Listener exceptions are swallowed.
 * - Re-`completeAwait` overwrites the stored value; already-run callbacks
 *   do not run again (intentional).
 *
 * Values are `unknown` here; callers with typed signals (e.g. CONTENT
 * completing with the active post) narrow at their own boundary.
 */

export enum AwaitFor {
  CONFIG = 'CONFIG',
  CONSENT = 'CONSENT',
  IDENTITY = 'IDENTITY',
  READY = 'READY',
  /** Pool content fetch idle (all in-flight fetches finished). */
  CONTENT = 'CONTENT',
}

type Trigger = (value: unknown) => void;

const completedAwaits = new Map<AwaitFor, unknown>();
const awaitingTriggers: Partial<{ [K in AwaitFor]: Trigger[] }> = {};

export function awaitFor(signal: AwaitFor, trigger: Trigger): void {
  if (completedAwaits.has(signal)) {
    try {
      trigger(completedAwaits.get(signal));
    } catch {
      /* swallow */
    }
    return;
  }
  (awaitingTriggers[signal] ??= []).push(trigger);
}

export function completeAwait(signal: AwaitFor, value?: unknown): void {
  completedAwaits.set(signal, value);
  const queued = awaitingTriggers[signal];
  if (queued && queued.length > 0) {
    delete awaitingTriggers[signal];
    for (const trigger of queued) {
      try {
        trigger(value);
      } catch {
        /* swallow */
      }
    }
  }
}

export function isAwaitCompleted(signal: AwaitFor): boolean {
  return completedAwaits.has(signal);
}

/** Test-only — clear all completed signals and pending queues. */
export function _resetAwaiter(): void {
  completedAwaits.clear();
  for (const key of Object.keys(awaitingTriggers) as AwaitFor[]) {
    delete awaitingTriggers[key];
  }
}
